import { useEffect, useState } from 'react';
import { CheckCircle2, Download, Wallet } from 'lucide-react';
import { financialApi, type PayrollPreview, type PayrollPreviewRow, type PayrollRun } from '../api';
import { StatusPill } from '@/lib/ui';

function money(minor: number, currency = 'AED'): string {
  const major = (minor / 100).toFixed(2);
  return `${currency} ${major}`;
}

function nextPeriodSuggestion(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function PayrollPage() {
  const [runs, setRuns] = useState<PayrollRun[]>([]);
  const [selected, setSelected] = useState<PayrollRun | null>(null);
  const [preview, setPreview] = useState<PayrollPreview | null>(null);
  const [openPeriod, setOpenPeriod] = useState(nextPeriodSuggestion());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmFinalize, setConfirmFinalize] = useState(false);

  const loadRuns = async () => {
    try {
      setRuns(await financialApi.listRuns(36));
    } catch (e) {
      setError((e as Error).message || 'Failed to load runs');
    }
  };

  const loadPreview = async (run: PayrollRun) => {
    setBusy(true);
    setError(null);
    try {
      setSelected(run);
      const p = await financialApi.previewRun(run.id);
      setPreview(p);
    } catch (e) {
      setError((e as Error).message || 'Failed to preview');
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  const openRun = async () => {
    if (!openPeriod.match(/^\d{4}-\d{2}$/)) {
      setError('Period must be YYYY-MM');
      return;
    }
    setBusy(true);
    try {
      const run = await financialApi.openRun(openPeriod);
      await loadRuns();
      await loadPreview(run);
    } catch (e) {
      setError((e as Error).message || 'Failed to open run');
    } finally {
      setBusy(false);
    }
  };

  const finalizeRun = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const run = await financialApi.finalizeRun(selected.id);
      setSelected(run);
      await loadRuns();
      await loadPreview(run);
      setConfirmFinalize(false);
    } catch (e) {
      setError((e as Error).message || 'Failed to finalize');
    } finally {
      setBusy(false);
    }
  };

  const downloadWps = async () => {
    if (!selected) return;
    const blob = await financialApi.downloadWpsCsv(selected.id);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wps-${selected.period}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    void loadRuns();
  }, []);

  return (
    <div className="flex h-full">
      {/* Left rail: runs list */}
      <aside className="w-72 overflow-y-auto border-r border-border bg-surface p-4">
        <h2 className="section-label">
          <Wallet className="h-3.5 w-3.5" />
          Payroll Runs
        </h2>
        <div className="mt-3 flex gap-2">
          <input
            value={openPeriod}
            onChange={(e) => setOpenPeriod(e.target.value)}
            className="input flex-1"
            placeholder="YYYY-MM"
            disabled={busy}
          />
          <button className="btn-primary" onClick={openRun} disabled={busy}>
            Open
          </button>
        </div>
        <ul className="mt-4 space-y-1">
          {runs.map((r) => (
            <li key={r.id}>
              <button
                onClick={() => void loadPreview(r)}
                className={`w-full rounded-md px-3 py-2 text-left transition-colors hover:bg-muted ${selected?.id === r.id ? 'bg-muted ring-1 ring-primary/30' : ''}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{r.period}</span>
                  <StatusPill status={r.status} />
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {r.employee_count} employees · {money(r.total_net_minor, r.currency)}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      {/* Main: preview */}
      <main className="flex-1 overflow-y-auto p-6">
        {error && (
          <div className="mb-4 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
            {error}
          </div>
        )}

        {!selected && (
          <div className="py-24 text-center text-muted-foreground">
            Select or open a payroll run to preview.
          </div>
        )}

        {selected && (
          <>
            <header className="page-header mb-6">
              <div>
                <h1 className="page-title flex items-center gap-2.5">
                  <Wallet className="h-6 w-6 text-primary" />
                  Payroll · {selected.period}
                </h1>
                <p className="page-subtitle flex flex-wrap items-center gap-2">
                  <span>
                    {selected.period_start} → {selected.period_end} · {selected.currency}
                  </span>
                  <StatusPill status={selected.status} />
                </p>
              </div>
              <div className="flex gap-2">
                {selected.status === 'FINALIZED' && (
                  <button className="btn-secondary" onClick={downloadWps}>
                    <Download className="h-4 w-4" />
                    Download WPS CSV
                  </button>
                )}
                {selected.status === 'DRAFT' && (
                  <button
                    className="btn-primary"
                    onClick={() => setConfirmFinalize(true)}
                    disabled={busy || !preview || preview.rows.some((r) => r.error)}
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    Finalize
                  </button>
                )}
              </div>
            </header>

            {preview && (
              <>
                <div className="mb-6 grid grid-cols-4 gap-4">
                  <KpiCard label="Employees" value={String(preview.employee_count)} />
                  <KpiCard
                    label="Gross"
                    value={money(preview.total_gross_minor, preview.currency)}
                  />
                  <KpiCard
                    label="Deductions"
                    value={money(preview.total_deductions_minor, preview.currency)}
                  />
                  <KpiCard
                    label="Net"
                    value={money(preview.total_net_minor, preview.currency)}
                    highlight
                  />
                </div>

                <PreviewTable rows={preview.rows} currency={preview.currency} />
              </>
            )}
          </>
        )}
      </main>

      {confirmFinalize && selected && (
        <FinalizeModal
          period={selected.period}
          totalNetMinor={preview?.total_net_minor ?? 0}
          currency={selected.currency}
          onCancel={() => setConfirmFinalize(false)}
          onConfirm={finalizeRun}
          busy={busy}
        />
      )}
    </div>
  );
}

function KpiCard({
  label,
  value,
  highlight = false,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`rounded border border-border bg-surface p-4 ${highlight ? 'ring-1 ring-primary' : ''}`}
    >
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 font-mono text-xl font-semibold">{value}</div>
    </div>
  );
}

function PreviewTable({ rows, currency }: { rows: PayrollPreviewRow[]; currency: string }) {
  const [expanded, setExpanded] = useState<string | null>(null);
  return (
    <div className="overflow-hidden rounded border border-border bg-surface">
      <table className="w-full text-sm">
        <thead className="bg-surface-accent text-xs uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="px-3 py-2 text-left">Employee</th>
            <th className="px-3 py-2 text-right">Base</th>
            <th className="px-3 py-2 text-right">Allow.</th>
            <th className="px-3 py-2 text-right">OT</th>
            <th className="px-3 py-2 text-right">Comm.</th>
            <th className="px-3 py-2 text-right">Adv.</th>
            <th className="px-3 py-2 text-right">Fines</th>
            <th className="px-3 py-2 text-right">Net</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r) => (
            <>
              <tr
                key={r.field_user_id}
                className={`hover:bg-surface-accent cursor-pointer ${r.error ? 'bg-rose-500/5' : ''}`}
                onClick={() => setExpanded(expanded === r.field_user_id ? null : r.field_user_id)}
              >
                <td className="px-3 py-2">
                  <div className="font-medium">{r.full_name}</div>
                  <div className="text-xs text-muted-foreground">{r.employee_id}</div>
                  {r.error && <div className="mt-1 text-xs text-rose-300">⚠ {r.error}</div>}
                </td>
                <td className="px-3 py-2 text-right font-mono">{money(r.base_minor, currency)}</td>
                <td className="px-3 py-2 text-right font-mono">
                  {money(r.allowances_minor, currency)}
                </td>
                <td className="px-3 py-2 text-right font-mono">
                  {money(r.overtime_minor, currency)}
                </td>
                <td className="px-3 py-2 text-right font-mono">
                  {money(r.commission_minor, currency)}
                </td>
                <td className="px-3 py-2 text-right font-mono text-rose-300">
                  -{money(r.advances_minor, currency)}
                </td>
                <td className="px-3 py-2 text-right font-mono text-rose-300">
                  -{money(r.fines_minor, currency)}
                </td>
                <td className="px-3 py-2 text-right font-mono font-semibold">
                  {money(r.net_minor, currency)}
                </td>
              </tr>
              {expanded === r.field_user_id && (
                <tr key={`${r.field_user_id}-trace`}>
                  <td colSpan={8} className="bg-background px-3 py-3">
                    <div className="grid grid-cols-2 gap-6 text-xs">
                      <div>
                        <div className="mb-2 font-semibold uppercase tracking-wider text-muted-foreground">
                          Formula
                        </div>
                        <code className="block whitespace-pre-wrap rounded bg-surface p-2 font-mono">
                          {r.formula_text}
                        </code>
                      </div>
                      <div>
                        <div className="mb-2 font-semibold uppercase tracking-wider text-muted-foreground">
                          Variables
                        </div>
                        <dl className="grid grid-cols-2 gap-x-3 gap-y-1 font-mono">
                          {Object.entries(r.variables).map(([k, v]) => (
                            <div key={k} className="flex justify-between">
                              <dt className="text-muted-foreground">{k}</dt>
                              <dd>{v}</dd>
                            </div>
                          ))}
                        </dl>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FinalizeModal({
  period,
  totalNetMinor,
  currency,
  onCancel,
  onConfirm,
  busy,
}: {
  period: string;
  totalNetMinor: number;
  currency: string;
  onCancel: () => void;
  onConfirm: () => void;
  busy: boolean;
}) {
  const [ack, setAck] = useState(false);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-96 rounded border border-border bg-surface p-6">
        <h3 className="text-lg font-semibold">Finalize Payroll</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Finalizing <strong>{period}</strong> writes immutable payslip rows + marks
          advances/fines/commission as deducted. This cannot be undone.
        </p>
        <div className="bg-surface-accent mt-4 rounded p-3 text-center font-mono">
          Net total: <strong>{money(totalNetMinor, currency)}</strong>
        </div>
        <label className="mt-4 flex items-center gap-2 text-sm">
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />I
          confirm payroll for {period}
        </label>
        <div className="mt-6 flex justify-end gap-2">
          <button className="btn-ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button className="btn-primary" onClick={onConfirm} disabled={!ack || busy}>
            {busy ? 'Finalizing…' : 'Finalize'}
          </button>
        </div>
      </div>
    </div>
  );
}
